/**
 * ModifierBag — a collection of modifiers keyed by stat name.
 *
 * Game-agnostic. Any system (attributes, skills, items, buffs) can push
 * modifiers in. Any consumer can query the resolved value for a stat.
 *
 * Features:
 *  - Add/remove individual modifiers
 *  - Remove all modifiers from a given source (e.g. remove all "Iron Ring" mods)
 *  - Lazy recalculation with dirty flag per stat
 *  - Hooks: event-driven callbacks for conditional logic (e.g. "on word scored")
 */

import { Modifier, ModOp, resolve, breakdown, ResolveBreakdown } from "./StatResolver";

/** Opaque handle returned by add(), used for targeted removal. */
export type ModHandle = number;

/** A hook callback registered against a named event. */
export interface Hook {
  /** The hook event name (e.g. "onWordScored"). */
  event: string;
  /** The callback function. Signature depends on the event. */
  fn: (...args: any[]) => any;
  /** Human-readable source for debugging / tooltips. */
  source: string;
}

/** Internal modifier entry with handle for removal. */
interface ModEntry extends Modifier {
  handle: ModHandle;
  stat: string;
}

export class ModifierBag {
  private nextHandle: ModHandle = 1;

  /** All modifiers, indexed by stat key. */
  private mods: Map<string, ModEntry[]> = new Map();

  /** Cached resolved values. */
  private cache: Map<string, number> = new Map();
  /** Dirty flags per stat. */
  private dirty: Set<string> = new Set();

  /** All hooks, indexed by event name. */
  private hooks: Map<string, Hook[]> = new Map();

  // --- Modifiers ---

  /** Add a modifier. Returns a handle for targeted removal. */
  add(stat: string, op: ModOp, value: number, source: string): ModHandle {
    const handle = this.nextHandle++;
    const entry: ModEntry = { op, value, source, handle, stat };

    if (!this.mods.has(stat)) this.mods.set(stat, []);
    this.mods.get(stat)!.push(entry);
    this.dirty.add(stat);

    return handle;
  }

  /** Remove a specific modifier by its handle. */
  remove(handle: ModHandle): void {
    for (const [stat, entries] of this.mods) {
      const idx = entries.findIndex(e => e.handle === handle);
      if (idx !== -1) {
        entries.splice(idx, 1);
        this.dirty.add(stat);
        return;
      }
    }
  }

  /** Remove all modifiers (and hooks) from a given source string. */
  removeBySource(source: string): void {
    for (const [stat, entries] of this.mods) {
      const before = entries.length;
      const filtered = entries.filter(e => e.source !== source);
      if (filtered.length !== before) {
        this.mods.set(stat, filtered);
        this.dirty.add(stat);
      }
    }
    for (const [event, hookList] of this.hooks) {
      this.hooks.set(event, hookList.filter(h => h.source !== source));
    }
  }

  /** Get the resolved value for a stat, given a base value. */
  get(stat: string, base: number = 0): number {
    if (!this.dirty.has(stat) && this.cache.has(stat)) {
      // Cache key includes base — if base changes, we need to recompute
      // For simplicity, always recompute. The cost is minimal.
    }
    const mods = this.mods.get(stat) ?? [];
    const val = resolve(base, mods);
    this.cache.set(stat, val);
    this.dirty.delete(stat);
    return val;
  }

  /** Get a full breakdown for a stat (for tooltips / damage display). */
  getBreakdown(stat: string, base: number = 0): ResolveBreakdown {
    const mods = this.mods.get(stat) ?? [];
    return breakdown(base, mods);
  }

  /** Get all modifiers for a stat (for inspection / display). */
  getModifiers(stat: string): readonly Modifier[] {
    return this.mods.get(stat) ?? [];
  }

  /** Check if any modifiers exist for a stat. */
  has(stat: string): boolean {
    const entries = this.mods.get(stat);
    return !!entries && entries.length > 0;
  }

  // --- Hooks ---

  /** Register a hook callback for a named event. */
  addHook(event: string, fn: (...args: any[]) => any, source: string): void {
    if (!this.hooks.has(event)) this.hooks.set(event, []);
    this.hooks.get(event)!.push({ event, fn, source });
  }

  /** Get all hooks for a named event. */
  getHooks(event: string): readonly Hook[] {
    return this.hooks.get(event) ?? [];
  }

  /** Run all hooks for an event, passing args. Returns array of results. */
  runHooks(event: string, ...args: any[]): any[] {
    const hookList = this.hooks.get(event) ?? [];
    return hookList.map(h => h.fn(...args));
  }

  // --- Bulk ---

  /** Clear everything (used when rebuilding at battle start). */
  clear(): void {
    this.mods.clear();
    this.cache.clear();
    this.dirty.clear();
    this.hooks.clear();
  }
}
